import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft,
  Save,
  X,
  Plus,
  ChevronDown,
  Search,
  Database,
  FileText,
  Clock,
  Target,
  AlertTriangle
} from 'lucide-react';
import { useToast, ToastContainer } from '../common/Toast';
import API_CONFIG from '../../config/api';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import FormStepper from '../common/FormStepper';

const CreateMilestone = () => {
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();
  
  const [currentStep, setCurrentStep] = useState(0);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    projectId: '',
    status: 'pending',
    priority: 'medium',
    startDate: '',
    dueDate: '',
    assignedTo: '',
    deliverables: [],
    dependencies: [],
    risks: [],
    estimatedHours: '',
    actualHours: '',
    notes: ''
  });

  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [testMode, setTestMode] = useState(false);
  
  // Searchable dropdown state for Project
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
  const projectDropdownRef = useRef(null);

  useEffect(() => {
    loadProjects();
    loadUsers();
    loadTestMode();
  }, []);

  const loadTestMode = async () => {
    try {
      const response = await fetch(API_CONFIG.ENDPOINTS.SETTINGS, {
        headers: API_CONFIG.HEADERS
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setTestMode(result.data.testMode ?? false);
        }
      }
    } catch (error) {
      console.error('Error loading test mode setting:', error);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (projectDropdownRef.current && !projectDropdownRef.current.contains(event.target)) {
        setIsProjectDropdownOpen(false);
      }
    };

    if (isProjectDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProjectDropdownOpen]);

  // Filter projects based on search term
  const filteredProjects = projects.filter(project =>
    project.name.toLowerCase().includes(projectSearchTerm.toLowerCase()) ||
    (project.projectCode && project.projectCode.toLowerCase().includes(projectSearchTerm.toLowerCase()))
  );

  // Get selected project name for display
  const selectedProject = projects.find(p => (p.id || p._id) === formData.projectId);

  const loadProjects = async () => {
    try {
      const response = await fetch(API_CONFIG.ENDPOINTS.PROJECTS, {
        headers: API_CONFIG.HEADERS
      });

      if (!response.ok) throw new Error('Failed to load projects');

      const result = await response.json();

      const projectsList =
        Array.isArray(result?.data)
          ? result.data
          : Array.isArray(result?.data?.projects)
          ? result.data.projects
          : Array.isArray(result?.projects)
          ? result.projects
          : [];

      setProjects(projectsList);
    } catch (error) {
      console.error('Error loading projects:', error);
      setProjects([]);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await fetch(API_CONFIG.ENDPOINTS.USERS, {
        headers: API_CONFIG.HEADERS
      });

      if (!response.ok) throw new Error('Failed to load users');

      const result = await response.json();

      const usersList =
        result?.data ||
        result?.users ||
        [];

      setUsers(Array.isArray(usersList) ? usersList : []);
    } catch (error) {
      console.error('Error loading users:', error);
      setUsers([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleArrayChange = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const handleAddArrayItem = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const handleRemoveArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (loading) {
      console.warn('Form submission already in progress, ignoring duplicate submit');
      return;
    }
    
    console.log('Form submitted', formData);
    setLoading(true);

    if (!formData.projectId) {
      console.log('Validation failed: No project selected');
      showError('Please select a project');
      setLoading(false);
      return;
    }

    if (!formData.name) {
      console.log('Validation failed: No milestone name');
      showError('Please enter a milestone name');
      setLoading(false);
      return;
    }

    try {
      const requestBody = {
        ...formData,
        projectId: formData.projectId
      };
      console.log('Sending request to:', `${API_CONFIG.ENDPOINTS.PROJECTS}/milestones`);
      console.log('Request body:', requestBody);

      const response = await fetch(`${API_CONFIG.ENDPOINTS.PROJECTS}/milestones`, {
        method: 'POST',
        headers: API_CONFIG.HEADERS,
        body: JSON.stringify(requestBody)
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (response.ok) {
        const responseData = await response.json();
        console.log('Success response:', responseData);
        success('Milestone created successfully!');
        navigate('/projects/milestone-tracking');
      } else {
        let errorMessage = 'Failed to create milestone';
        try {
          const errorData = await response.json();
          console.log('Error response:', errorData);
          errorMessage = errorData.message || errorMessage;
        } catch (parseError) {
          console.log('Failed to parse error response:', parseError);
          errorMessage = response.statusText || `Server returned ${response.status} error`;
        }
        console.log('Showing error:', errorMessage);
        showError(errorMessage);
      }
    } catch (error) {
      console.error('Error creating milestone:', error);
      showError(error.message || 'Error creating milestone. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = (e) => {
    e.preventDefault();
    setCurrentStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const fillTestData = () => {
    const firstProjectId = projects.length > 0 ? projects[0].id : '';
    const firstUserId = users.length > 0 ? users[0].id : '';
    
    const today = new Date();
    const startDate = new Date(today);
    startDate.setDate(today.getDate() + 7);
    
    const dueDate = new Date(today);
    dueDate.setMonth(today.getMonth() + 3);

    const testData = {
      name: 'Foundation & Structure Completion',
      description: 'Complete foundation work and structural framework for the luxury residential development. This milestone includes excavation, concrete pouring, steel framework installation, and structural inspections.',
      projectId: firstProjectId,
      status: 'in-progress',
      priority: 'high',
      startDate: startDate.toISOString().split('T')[0],
      dueDate: dueDate.toISOString().split('T')[0],
      assignedTo: firstUserId,
      deliverables: [
        'Foundation excavation complete',
        'Concrete foundation poured and cured',
        'Structural steel framework erected',
        'All structural inspections passed',
        'Engineering sign-off received'
      ],
      dependencies: [
        'Site Preparation & Permits milestone completion',
        'Architectural drawings approval',
        'Building permits obtained'
      ],
      risks: [
        'Weather delays affecting concrete pouring schedule',
        'Material delivery delays for steel framework',
        'Inspection scheduling conflicts',
        'Soil condition variations requiring design adjustments'
      ],
      estimatedHours: '320',
      actualHours: '85',
      notes: 'This is a critical milestone for the project timeline. Regular progress meetings will be held weekly to monitor advancement.'
    };
    
    setFormData(testData);
    success('Test data filled successfully!');
  };

  const STEPS = [
    { id: 0, label: 'Milestone Setup', icon: FileText, hint: 'Name, project and basics' },
    { id: 1, label: 'Timeline', icon: Clock, hint: 'Dates, assignment and effort' },
    { id: 2, label: 'Deliverables', icon: Target, hint: 'Deliverables and dependencies' },
    { id: 3, label: 'Risks', icon: AlertTriangle, hint: 'Risks and additional notes' },
  ];

  const renderBasicInformation = () => (
    <div className="task-form-section">
      <div className="section-header">
        <h3 className="section-title">Basic Information</h3>
        <p className="section-description">Milestone name, description, and basic details</p>
      </div>
      
      {/* Project Field */}
      <div className="form-group">
        <label className="form-label">Project *</label>
        <div ref={projectDropdownRef} style={{ position: 'relative' }}>
          <div
            className="form-control"
            style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              padding: '8px 12px',
              minHeight: '38px'
            }}
            onClick={() => setIsProjectDropdownOpen(!isProjectDropdownOpen)}
          >
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
              {selectedProject ? (
                <span style={{ color: '#1f2937' }}>
                  {selectedProject.name} {selectedProject.projectCode ? `(${selectedProject.projectCode})` : ''}
                </span>
              ) : formData.projectId ? (
                <span style={{ color: '#9ca3af' }}>Project ID: {formData.projectId} (not found)</span>
              ) : (
                <span style={{ color: '#9ca3af' }}>Select Project</span>
              )}
            </div>
            <ChevronDown 
              size={18} 
              style={{ 
                color: '#6b7280',
                transform: isProjectDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s'
              }} 
            />
          </div>
          
          {isProjectDropdownOpen && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              backgroundColor: 'white',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              zIndex: 1000,
              marginTop: '4px',
              maxHeight: '300px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <div style={{ 
                padding: '8px',
                borderBottom: '1px solid #e5e7eb',
                position: 'sticky',
                top: 0,
                backgroundColor: 'white'
              }}>
                <div style={{ position: 'relative' }}>
                  <Search 
                    size={16} 
                    style={{ 
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#9ca3af'
                    }} 
                  />
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search projects..."
                    value={projectSearchTerm}
                    onChange={(e) => setProjectSearchTerm(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      paddingLeft: '36px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px'
                    }}
                    autoFocus
                  />
                </div>
              </div>
              
              <div style={{ overflowY: 'auto', maxHeight: '250px' }}>
                {filteredProjects.length > 0 ? (
                  filteredProjects.map(project => {
                    const projectId = project.id || project._id;
                    return (
                      <div
                        key={projectId}
                        onClick={() => {
                          console.log('Project selected:', project, 'ID:', projectId);
                          setFormData(prev => {
                            const updated = { ...prev, projectId: projectId };
                            console.log('Updated formData:', updated);
                            return updated;
                          });
                          setIsProjectDropdownOpen(false);
                          setProjectSearchTerm('');
                        }}
                        style={{
                          padding: '10px 12px',
                          cursor: 'pointer',
                          backgroundColor: formData.projectId === projectId ? '#eff6ff' : 'white',
                          borderBottom: '1px solid #f3f4f6',
                          transition: 'background-color 0.15s'
                        }}
                        onMouseEnter={(e) => {
                          if (formData.projectId !== projectId) {
                            e.target.style.backgroundColor = '#f9fafb';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (formData.projectId !== projectId) {
                            e.target.style.backgroundColor = 'white';
                          }
                        }}
                      >
                        <div style={{ fontWeight: formData.projectId === projectId ? '600' : '400', color: '#1f2937' }}>
                          {project.name}
                        </div>
                        {project.projectCode && (
                          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                            {project.projectCode}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div style={{
                    padding: '16px 12px',
                    textAlign: 'center',
                    color: '#6b7280',
                    fontSize: '14px'
                  }}>
                    No projects found
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        {!formData.projectId && (
          <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
            Project is required
          </div>
        )}
      </div>

      <div className="form-group">
        <label className="form-label">Milestone Name *</label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          className="form-control"
          placeholder="Enter milestone name"
          required
        />
      </div>
      
      <div className="form-group">
        <label className="form-label">Description</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          className="form-control"
          placeholder="Describe the milestone objectives and scope"
          rows={4}
        />
      </div>

      <div className="form-grid-3">
        <div className="form-group">
          <label className="form-label">Status</label>
          <select
            name="status"
            value={formData.status}
            onChange={handleInputChange}
            className="form-select"
          >
            <option value="pending">Pending</option>
            <option value="in-progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="on-hold">On Hold</option>
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Priority</label>
          <select
            name="priority"
            value={formData.priority}
            onChange={handleInputChange}
            className="form-select"
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
        </div>
      </div>
    </div>
  );

  const renderTimelineResources = () => (
    <div className="task-form-section">
      <div className="section-header">
        <h3 className="section-title">Timeline</h3>
        <p className="section-description">Set milestone start and due dates</p>
      </div>
      
      <div className="form-grid-2">
        <div className="form-group">
          <label className="form-label">Start Date</label>
          <input
            type="date"
            name="startDate"
            value={formData.startDate}
            onChange={handleInputChange}
            className="form-control"
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Due Date *</label>
          <input
            type="date"
            name="dueDate"
            value={formData.dueDate}
            onChange={handleInputChange}
            className="form-control"
            required
          />
        </div>
      </div>

      <div className="section-header" style={{ marginTop: '32px' }}>
        <h3 className="section-title">Assignment</h3>
        <p className="section-description">Assign responsibility for this milestone</p>
      </div>
      
      <div className="form-group">
        <label className="form-label">Assigned To *</label>
        <select
          name="assignedTo"
          value={formData.assignedTo}
          onChange={handleInputChange}
          className="form-select"
          required
        >
          <option value="">Select assignee</option>
          {users.map(user => {
            const userId = user._id || user.id;
            return (
              <option key={userId} value={userId}>
                {`${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unnamed User'}
              </option>
            );
          })}
        </select>
      </div>

      <div className="section-header" style={{ marginTop: '32px' }}>
        <h3 className="section-title">Time Tracking</h3>
        <p className="section-description">Track estimated and actual hours</p>
      </div>
      
      <div className="form-grid-2">
        <div className="form-group">
          <label className="form-label">Estimated Hours</label>
          <input
            type="number"
            name="estimatedHours"
            value={formData.estimatedHours}
            onChange={handleInputChange}
            className="form-control"
            placeholder="0"
            min="0"
            step="0.5"
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Actual Hours</label>
          <input
            type="number"
            name="actualHours"
            value={formData.actualHours}
            onChange={handleInputChange}
            className="form-control"
            placeholder="0"
            min="0"
            step="0.5"
          />
        </div>
      </div>
    </div>
  );

  const renderDeliverablesDependencies = () => (
    <div className="task-form-section">
      <div className="section-header">
        <h3 className="section-title">Deliverables</h3>
        <p className="section-description">Define key deliverables for this milestone</p>
      </div>
      
      <div className="form-group">
        <label className="form-label">Deliverables</label>
        <div className="array-input-group">
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Add deliverable"
              onKeyPress={(e) => {
  if (e.key === 'Enter') {
    e.preventDefault();
    const value = e.target.value.trim();
    if (!value) return;

    setFormData(prev => ({
      ...prev,
      deliverables: [...prev.deliverables, value]
    }));

    e.target.value = '';
  }
}}
            />
            <button
              type="button"
              className="btn btn-outline"
              onClick={(e) => {
                const input = e.target.parentElement.querySelector('input');
                if (input && input.value.trim()) {
                  const value = input.value.trim();
if (!value) return;

setFormData(prev => ({
  ...prev,
  deliverables: [...prev.deliverables, value]
}));

input.value = '';
                }
              }}
            >
              <Plus size={16} />
            </button>
          </div>
          <div className="array-items">
            {formData.deliverables.map((deliverable, index) => (
              <div key={index} className="array-item">
                <input
                  type="text"
                  value={deliverable}
                  onChange={(e) => handleArrayChange('deliverables', index, e.target.value)}
                  className="form-control"
                  placeholder="Enter deliverable"
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  onClick={() => handleRemoveArrayItem('deliverables', index)}
                  className="btn btn-sm btn-outline-danger"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="section-header" style={{ marginTop: '32px' }}>
        <h3 className="section-title">Dependencies</h3>
        <p className="section-description">List any dependencies for this milestone</p>
      </div>
      
      <div className="form-group">
        <label className="form-label">Dependencies</label>
        <div className="array-input-group">
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Add dependency"
              onKeyPress={(e) => {
  if (e.key === 'Enter') {
    e.preventDefault();
    const value = e.target.value.trim();
    if (!value) return;

    setFormData(prev => ({
      ...prev,
      dependencies: [...prev.dependencies, value]
    }));

    e.target.value = '';
  }
}}

            />
            <button
              type="button"
              className="btn btn-outline"
              onClick={(e) => {
                const input = e.target.parentElement.querySelector('input');
                if (input && input.value.trim()) {
                  const value = input.value.trim();
if (!value) return;

setFormData(prev => ({
  ...prev,
  dependencies: [...prev.dependencies, value]
}));

input.value = '';

                }
              }}
            >
              <Plus size={16} />
            </button>
          </div>
          <div className="array-items">
            {formData.dependencies.map((dependency, index) => (
              <div key={index} className="array-item">
                <input
                  type="text"
                  value={dependency}
                  onChange={(e) => handleArrayChange('dependencies', index, e.target.value)}
                  className="form-control"
                  placeholder="Enter dependency"
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  onClick={() => handleRemoveArrayItem('dependencies', index)}
                  className="btn btn-sm btn-outline-danger"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderRisksTracking = () => (
    <div className="task-form-section">
      <div className="section-header">
        <h3 className="section-title">Risks</h3>
        <p className="section-description">Identify potential risks for this milestone</p>
      </div>
      
      <div className="form-group">
        <label className="form-label">Risks</label>
        <div className="array-input-group">
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Add risk"
              onKeyPress={(e) => {
  if (e.key === 'Enter') {
    e.preventDefault();
    const value = e.target.value.trim();
    if (!value) return;

    setFormData(prev => ({
      ...prev,
      risks: [...prev.risks, value]
    }));

    e.target.value = '';
  }
}}
            />
            <button
              type="button"
              className="btn btn-outline"
              onClick={(e) => {
                const input = e.target.parentElement.querySelector('input');
                if (input && input.value.trim()) {
                  const value = input.value.trim();
if (!value) return;

setFormData(prev => ({
  ...prev,
  risks: [...prev.risks, value]
}));

input.value = '';

                }
              }}
            >
              <Plus size={16} />
            </button>
          </div>
          <div className="array-items">
            {formData.risks.map((risk, index) => (
              <div key={index} className="array-item">
                <input
                  type="text"
                  value={risk}
                  onChange={(e) => handleArrayChange('risks', index, e.target.value)}
                  className="form-control"
                  placeholder="Enter risk"
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  onClick={() => handleRemoveArrayItem('risks', index)}
                  className="btn btn-sm btn-outline-danger"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="section-header" style={{ marginTop: '32px' }}>
        <h3 className="section-title">Additional Notes</h3>
        <p className="section-description">Add any additional notes or comments</p>
      </div>
      
      <div className="form-group">
        <textarea
          name="notes"
          value={formData.notes}
          onChange={handleInputChange}
          className="form-control"
          placeholder="Add any additional notes or comments"
          rows={6}
        />
      </div>
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return renderBasicInformation();
      case 1:
        return renderTimelineResources();
      case 2:
        return renderDeliverablesDependencies();
      case 3:
        return renderRisksTracking();
      default:
        return renderBasicInformation();
    }
  };

  return (
    <>
      <PageBreadcrumb />
      <PageHeader 
        title="Create New Milestone"
        description="Define a new project milestone with all necessary details"
      />
      <div className="page-container" style={{ width: '100%', maxWidth: '100%' }}>
        <div className="project-create-container" style={{ width: '100%', maxWidth: '100%' }}>
          <form onSubmit={currentStep === STEPS.length - 1 ? handleSubmit : handleNext} noValidate style={{ width: '100%' }}>
            <div style={{ width: '100%', marginBottom: '24px' }}>
              <FormStepper
                steps={STEPS}
                currentStep={currentStep}
                onStepClick={(index) => {
                  if (index <= currentStep) setCurrentStep(index);
                }}
              />
              <div style={{ marginTop: '12px', color: '#6b7280', fontSize: '14px' }}>
                {STEPS[currentStep]?.hint}
              </div>
            </div>

            <div className="project-form-card">
              {renderStepContent()}
            </div>

            {/* Form Actions */}
            <div className="project-form-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
              {testMode && (
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={fillTestData}
                  style={{ marginRight: 'auto' }}
                >
                  <Database size={18} />
                  Fill Test Data
                </button>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginLeft: testMode ? '0' : 'auto' }}>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => navigate('/projects/milestone-tracking')}
                  style={{ minWidth: '110px' }}
                >
                  Cancel
                </button>
                {currentStep > 0 && (
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={handleBack}
                    style={{ minWidth: '110px' }}
                  >
                    Back
                  </button>
                )}
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                  style={{ minWidth: '140px' }}
                >
                  {loading ? (
                    <>
                      <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      Creating Milestone...
                    </>
                  ) : currentStep < STEPS.length - 1 ? (
                    'Next'
                  ) : (
                    <>
                      <Save size={18} />
                      Create Milestone
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default CreateMilestone;